import { decompressFrames, parseGIF } from 'gifuct-js';
import { applyPalette, GIFEncoder, quantize } from 'gifenc';

const createCanvas = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

/**
 * Crops every frame of an animated GIF and re-encodes it, so the animation survives the crop.
 * `crop` is in the GIF's natural pixels (cropper.getData(true)). The output is scaled down to fit
 * maxWidth × maxHeight. Returns the original file when no crop or resize is needed.
 */
export const cropAnimatedGif = async (file, crop, { maxWidth = 2048, maxHeight = 1024 } = {}) => {
    const gif = parseGIF(await file.arrayBuffer());
    const frames = decompressFrames(gif, true);
    const gifWidth = gif.lsd.width;
    const gifHeight = gif.lsd.height;
    if (!frames.length) throw new Error('GIF has no frames');

    const x = Math.max(0, Math.round(crop.x));
    const y = Math.max(0, Math.round(crop.y));
    const cropWidth = Math.min(gifWidth - x, Math.round(crop.width));
    const cropHeight = Math.min(gifHeight - y, Math.round(crop.height));
    const scale = Math.min(1, maxWidth / cropWidth, maxHeight / cropHeight);
    const outWidth = Math.max(1, Math.round(cropWidth * scale));
    const outHeight = Math.max(1, Math.round(cropHeight * scale));

    if (x === 0 && y === 0 && cropWidth === gifWidth && cropHeight === gifHeight && scale === 1) {
        return file;
    }

    // Frames are patches over the previous frame, so composite them at full size before cropping
    const full = createCanvas(gifWidth, gifHeight);
    const fullCtx = full.getContext('2d');
    const patch = createCanvas(1, 1);
    const patchCtx = patch.getContext('2d');
    const out = createCanvas(outWidth, outHeight);
    const outCtx = out.getContext('2d');
    const encoder = GIFEncoder();

    frames.forEach((frame, index) => {
        const { dims, disposalType, delay } = frame;
        const previous = disposalType === 3 ? fullCtx.getImageData(0, 0, gifWidth, gifHeight) : null;

        patch.width = dims.width;
        patch.height = dims.height;
        patchCtx.putImageData(new ImageData(frame.patch, dims.width, dims.height), 0, 0);
        fullCtx.drawImage(patch, dims.left, dims.top);

        outCtx.clearRect(0, 0, outWidth, outHeight);
        outCtx.drawImage(full, x, y, cropWidth, cropHeight, 0, 0, outWidth, outHeight);
        const { data } = outCtx.getImageData(0, 0, outWidth, outHeight);

        const palette = quantize(data, 256, { format: 'rgba4444', oneBitAlpha: true });
        const indexed = applyPalette(data, palette, 'rgba4444');
        const transparentIndex = palette.findIndex((color) => color[3] === 0);

        encoder.writeFrame(indexed, outWidth, outHeight, {
            palette,
            delay: delay || 100,
            transparent: transparentIndex !== -1,
            transparentIndex: Math.max(0, transparentIndex),
            repeat: index === 0 ? 0 : undefined,
        });

        if (disposalType === 2) {
            fullCtx.clearRect(dims.left, dims.top, dims.width, dims.height);
        } else if (previous) {
            fullCtx.putImageData(previous, 0, 0);
        }
    });

    encoder.finish();
    return new File([encoder.bytes()], file.name, { type: 'image/gif' });
};
